import type { z } from 'zod';

/** One problem with one field. `path` uses dot notation so it maps straight onto form fields ("teamA.code", "segments.3.durationSec"). */
export interface FieldIssue {
  path: string;
  code: string;
  message: string;
}

export type AppError =
  | { kind: 'validation'; message: string; issues: FieldIssue[] }
  | { kind: 'notFound'; message: string; entity: string; id: string }
  | { kind: 'invalidState'; message: string; code: string };

export type Result<T, E = AppError> = { ok: true; value: T } | { ok: false; error: E };

export const ok = <T>(value: T): Result<T, never> => ({ ok: true, value });
export const err = <E>(error: E): Result<never, E> => ({ ok: false, error });

export const notFound = (entity: string, id: string): AppError => ({
  kind: 'notFound',
  entity,
  id,
  message: `That ${entity} no longer exists. It may have been deleted.`,
});

export const invalidState = (code: string, message: string): AppError => ({ kind: 'invalidState', code, message });

/** Human labels for fields, keyed by dot path with numeric segments replaced by `*` ("segments.*.label"). */
export type FieldLabels = Record<string, string>;

const labelFor = (path: (string | number)[], labels: FieldLabels): string => {
  const key = path.map((p) => (typeof p === 'number' ? '*' : p)).join('.');
  if (labels[key]) return labels[key];
  const last = [...path].reverse().find((p) => typeof p === 'string');
  if (!last) return 'This value';
  // camelCase → "Camel case"
  const words = String(last).replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const fmtSeconds = (n: number) => {
  const m = Math.floor(n / 60);
  const s = n % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

/** Turn one zod issue into a sentence a tired judge can act on at 7am. */
const plainMessage = (issue: z.core.$ZodIssue, label: string): string => {
  // Explicit messages written in schemas win.
  if (issue.message && !issue.message.startsWith('Invalid') && !issue.message.startsWith('Too ') && !issue.message.startsWith('Expected')) {
    return issue.message;
  }
  const seconds = /sec$/i.test(String(issue.path.at(-1) ?? ''));
  switch (issue.code) {
    case 'invalid_type':
      return issue.input === undefined || issue.input === null || issue.input === ''
        ? `${label} is required.`
        : `${label} has the wrong kind of value.`;
    case 'too_small': {
      const min = Number(issue.minimum);
      if (issue.origin === 'string') return min <= 1 ? `${label} is required.` : `${label} must be at least ${min} characters.`;
      if (issue.origin === 'array') return `${label} needs at least ${min} item${min === 1 ? '' : 's'}.`;
      return `${label} must be at least ${seconds ? fmtSeconds(min) : min}.`;
    }
    case 'too_big': {
      const max = Number(issue.maximum);
      if (issue.origin === 'string') return `${label} must be ${max} characters or fewer.`;
      if (issue.origin === 'array') return `${label} can have at most ${max} items.`;
      return `${label} must be at most ${seconds ? fmtSeconds(max) : max}.`;
    }
    case 'invalid_value':
      return `${label} must be one of: ${issue.values.map(String).join(', ')}.`;
    case 'invalid_format':
      return `${label} isn't in the expected format.`;
    case 'not_multiple_of':
      return `${label} must be in steps of ${issue.divisor}.`;
    case 'unrecognized_keys':
      return `Unexpected field${issue.keys.length === 1 ? '' : 's'}: ${issue.keys.join(', ')}.`;
    default:
      return issue.message || `${label} is invalid.`;
  }
};

/** Collect every issue (never just the first) as field-mappable, plain-language errors. */
export const toFieldIssues = (error: z.ZodError, labels: FieldLabels = {}): FieldIssue[] =>
  error.issues.map((issue) => ({
    path: issue.path.map(String).join('.'),
    code: issue.code,
    message: plainMessage(issue, labelFor(issue.path as (string | number)[], labels)),
  }));

export const validationError = (issues: FieldIssue[], message?: string): AppError => ({
  kind: 'validation',
  issues,
  message: message ?? (issues.length === 1 ? issues[0]!.message : `Please fix ${issues.length} problems.`),
});

/** Parse with a schema, returning all problems at once. */
export const validate = <S extends z.ZodType>(
  schema: S,
  input: unknown,
  labels: FieldLabels = {},
): Result<z.output<S>> => {
  const parsed = schema.safeParse(input);
  if (parsed.success) return ok(parsed.data);
  return err(validationError(toFieldIssues(parsed.error, labels)));
};

/** Shape suited to form libraries: { "teamA.code": "Team code is required." } (first message per field). */
export const issuesToFormErrors = (issues: FieldIssue[]): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const i of issues) if (!(i.path in out)) out[i.path] = i.message;
  return out;
};
